import { api } from "@/shared/services/api";
import { UserPersonalization } from "@/shared/lib/types";

// CR-032 §3.2 — migrated from raw fetch to the shared `ky` client (`api`) so
// this route gets Bearer-token injection + centralized 401 auto-logout via
// `services/api.ts`'s `afterResponse` hook, matching the convention the rest
// of the data layer uses.
//
// `throwHttpErrors: false` is intentional and NOT in the original solution
// snippet (SOL-032 §3.2) — added after tracing real callers (grep, since
// GitNexus impact()/context() returned corrupted/garbled candidates for this
// symbol name in this session — ambiguous match against unrelated Go/TS
// symbols, ignored per project CLAUDE.md guidance to verify manually instead
// of trusting a suspicious result). `ky` throws `HTTPError` on any non-2xx
// response by default, unlike `fetch` (which only rejects on network
// failure). Real call-sites depend on the old "always resolves to a
// Response" contract:
//   - UserProvider.tsx:261 checks `response.ok` and calls `refreshUser()`
//     in BOTH the success and failure branch before re-throwing — if this
//     resolved via a thrown HTTPError instead, `refreshUser()` would be
//     skipped on failure and optimistic (bad) state would stick.
//   - useShowOnboarding.ts:184 and NonAdminStep.tsx:37 import this function
//     directly (not through UserProvider) and currently never check
//     `.ok` at all — switching to ky's default throw-on-error would flip
//     their failure handling from a silent no-op to a caught exception,
//     an undisclosed behavior change out of scope for this task.
// Passing `throwHttpErrors: false` keeps this function's contract byte-for-
// byte identical to the old `fetch()` version (always resolves to a
// `Response`, callers keep checking `.ok`), so the "no call-site changes,
// LOW risk" claim in SOL-032 §3.2 actually holds.
export async function setUserDefaultModel(
  model: string | null
): Promise<Response> {
  // NOTE: PATCH /api/user/default-model still 404s on the backend — Related
  // Gap of CR-032, out of scope for this CR. Keep current behavior (don't
  // throw here; let the caller handle the response as before).
  return api.patch("user/default-model", {
    json: { default_model: model },
    throwHttpErrors: false,
  });
}

/**
 * Update the current user's personalization settings.
 * PATCH /api/user/personalization — full-replace, matches Backend SOL-032 §2.1.
 */
export async function updateUserPersonalization(
  personalization: Partial<UserPersonalization>
): Promise<Response> {
  return api.patch("user/personalization", {
    json: personalization,
    throwHttpErrors: false,
  });
}
